// Utilidades de RUT chileno.
// Referencia: algoritmo módulo 11 (Servicio de Impuestos Internos).

/** Limpia un RUT: quita puntos, espacios y pasa el dígito verificador a mayúscula. */
export function cleanRut(rut) {
  if (!rut) return '';
  return String(rut).trim().toUpperCase().replace(/\./g, '').replace(/\s+/g, '');
}

/** Normaliza un RUT al formato `XXXXXXXX-Y` (sin puntos, con guion, DV en mayúscula). */
export function normalizeRut(rut) {
  const s = cleanRut(rut);
  if (!s) return '';
  const raw = s.replace(/-/g, '');
  if (raw.length < 2) return s;
  return `${raw.slice(0, -1)}-${raw.slice(-1)}`;
}

/** Formatea un RUT como `XX.XXX.XXX-Y` para mostrar. */
export function formatRut(rut) {
  const s = cleanRut(rut).replace(/-/g, '');
  if (s.length < 2) return s;
  const body = s.slice(0, -1);
  const dv = s.slice(-1);
  const bodyFmt = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${bodyFmt}-${dv}`;
}

/**
 * Formateo "en vivo" mientras el usuario tipea.
 * - Elimina cualquier carácter que no sea dígito o K.
 * - Trata siempre el último carácter como dígito verificador.
 * - Inserta puntos cada 3 dígitos en el cuerpo y un guion antes del DV.
 * - Máximo 9 caracteres (8 dígitos + 1 DV).
 * Ejemplos: '1' → '1'; '12' → '1-2'; '12345' → '1.234-5'; '123456785' → '12.345.678-5'.
 */
export function formatRutInput(raw) {
  const s = String(raw || '').toUpperCase().replace(/[^0-9K]/g, '').slice(0, 9);
  if (s.length <= 1) return s;
  const body = s.slice(0, -1).replace(/K/g, ''); // K solo válida como DV
  const dv = s.slice(-1);
  const bodyFmt = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return bodyFmt ? `${bodyFmt}-${dv}` : dv;
}

/** Calcula el dígito verificador esperado para un cuerpo numérico. */
function computeDv(bodyDigits) {
  let sum = 0;
  let mul = 2;
  for (let i = bodyDigits.length - 1; i >= 0; i--) {
    sum += parseInt(bodyDigits[i], 10) * mul;
    mul = mul === 7 ? 2 : mul + 1;
  }
  const res = 11 - (sum % 11);
  if (res === 11) return '0';
  if (res === 10) return 'K';
  return String(res);
}

/**
 * Valida un RUT chileno con módulo 11.
 * Acepta con o sin puntos, con o sin guion. Rechaza:
 *  - vacío / muy corto / muy largo
 *  - cuerpo con no dígitos
 *  - DV que no coincide
 *  - RUT triviales tipo 11.111.111-1
 */
export function isValidRut(rut) {
  const s = cleanRut(rut).replace(/-/g, '');
  if (s.length < 8 || s.length > 9) return false;
  const body = s.slice(0, -1);
  const dv = s.slice(-1);
  if (!/^\d+$/.test(body)) return false;
  if (!/^[0-9K]$/.test(dv)) return false;
  // Rechazar cuerpos con todos los dígitos iguales (11111111, 22222222, etc.)
  if (/^(\d)\1+$/.test(body)) return false;
  return computeDv(body) === dv;
}
